import { Component, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import * as XLSX from 'xlsx';
import { DataSheetImportDef } from '../../shared/misc-classes';

@Component({
  selector: 'data-sheet-import-dialog',
  templateUrl: './data-sheet-import-dialog.html',
  styleUrls: ['./data-sheet-import-dialog.css']
})
export class DataSheetImportDialog {
  Hvr: any = {};
  errorMessage: string = '';
  dataStList: Array<{key: string, value: string, section?: string}> = [];

  searchOpts:DataSheetSearchParams = new DataSheetSearchParams( '', [], []);

  // serialData: Array<SerialData> = [];
  constructor(
    public dialogRef: MatDialogRef<DataSheetImportDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
  }

  optionSelected(){
    this.searchOpts.extractedDatasheets.length = 0;
    this.searchOpts.datasheets.length = 0;
  }

  //Scans every row: column A (index 0) is the label, column B (index 1) is the value.
  //case-insensitive, trimmed, Stores all entries so partial matching can be used later.
  private buildLabelMap(_jsonArr: Array<any>): Map<string, string> {
    const map = new Map<string, string>();
    for (let r = 0; r < _jsonArr.length; r++) {
      const row = _jsonArr[r];
      if (!row || row.length < 2) continue;
      const label = (row[0] != null ? String(row[0]) : '').trim();
      const value = (row[1] != null ? String(row[1]) : '').trim();
      if (label !== '') {
        const lowerLabel = label.toLowerCase();
        if (!map.has(lowerLabel)) {
          map.set(lowerLabel, value);
        }
      }
    }
    return map;
  }

  // inds a value from the label map (column A → column B).
  // Tries exact match first, then partial match (key contains term).
  private findByLabel(map: Map<string, string>, searchTerms: string | string[], excludeTerms?: string[]): string {
    const terms = Array.isArray(searchTerms) ? searchTerms : [searchTerms];
    const excludes = (excludeTerms || []).map(e => e.toLowerCase().trim());

    for (const searchTerm of terms) {
      const term = searchTerm.toLowerCase().trim();

      // Exact match
      if (map.has(term)) {
        const skip = excludes.some(ex => term.indexOf(ex) >= 0 && term !== ex);
        if (!skip) return map.get(term) || '';
      }

      // Partial match (only if exact match not found)
      let found = '';
      map.forEach((value, key) => {
        if (found) return; // already found
        if (excludes.some(ex => key.indexOf(ex) >= 0)) return;
        if (key.indexOf(term) >= 0) {
          found = value;
        }
      });
      if (found) return found;
    }

    return '';
  }

  //Scans all column-B values in the 2D array for a product-type identifier string.
  //Returns true if the identifier (e.g. "ZS1") is found anywhere in any column-B cell.
  private detectProductType(_jsonArr: Array<any>, identifier: string): boolean {
    for (let r = 0; r < _jsonArr.length; r++) {
      const row = _jsonArr[r];
      if (row && row[1] != null && typeof row[1] === 'string' && row[1].indexOf(identifier) >= 0) {
        return true;
      }
    }
    return false;
  }

  onFileChange(evt: any) {
    /* wire up file reader */
    const target: DataTransfer = <DataTransfer>(evt.target);
    if (target.files.length !== 1) throw new Error('Cannot use multiple files');
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, {type: 'binary'});

      var _tmpDS: Array<DataSheetImportDef> = [];

      this.searchOpts.extractedDatasheets.length = 0;
      this.searchOpts.datasheets.length = 0;
      this.dataStList.length = 0;
      this.errorMessage = '';

      const wsname: string = wb.SheetNames[0]; // 1st sheet is the datasheet
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];
      var _jsonArr: Array<any> = XLSX.utils.sheet_to_json(ws, { header: 1 });

      // Product type validation (scan column B for identifier)
      var _proceedFlag: boolean = false;
      switch (this.data.productType) {
        case "UG ZS1":
          _proceedFlag = this.detectProductType(_jsonArr, "ZS1");
          break;
        case "UG ZS2":
          _proceedFlag = this.detectProductType(_jsonArr, "ZS2");
          break;
        case "LG ZN1":
        case "LG ZS9":
          _proceedFlag = this.detectProductType(_jsonArr, "ZN1") || this.detectProductType(_jsonArr, "ZS9");
          break;
      }

      if (!_proceedFlag) {
        this.errorMessage = "The selected datasheet does not match the selected product type.";
        return;
      }

      //Build label→value lookup from column A → column B
      const labelMap = this.buildLabelMap(_jsonArr);

      //Extract fields by label (case-insensitive, partial match on column A → column B)
      var _tmpSysVolt = this.findByLabel(labelMap, ["service voltage", "system voltage"]);
      var _tmpRatVolt = this.findByLabel(labelMap, ["rated voltage"]);
      var _tmpDryFrq = this.findByLabel(labelMap, ["power frequency withstand"]);
      var _tmpRatImpVolt = this.findByLabel(labelMap, ["bil", "lightning impulse", "impulse withstand voltage"]);
      var _tmpFrq = this.findByLabel(labelMap, ["rated frequency"]);
      var _tmpShtTimeCrRat = this.findByLabel(labelMap, ["short time withstand current for main circuit", "short time withstand current"]);
      var _tmpAlt = this.findByLabel(labelMap, ["altitude"]);
      var _tmpBusbarSystem = this.findByLabel(labelMap, ["unigear type"]);
      var _tmpSwtCrRat = this.findByLabel(labelMap, ["busbar rated current"]);
      var _tmpDynmPeakCrr = this.findByLabel(labelMap, ["peak withstand current"]);
      var _tmpAmbTmp = this.findByLabel(labelMap, ["ambient temperature"]);
      var _tmpIntArc = this.findByLabel(labelMap, ["internal arc withstand"]);
      var _tmpEnclosure = this.findByLabel(labelMap, ["protection degree outer cover"]);
      var _tmpSprgChrgMotor = this.findByLabel(labelMap, ["spring loading", "spring charging"]);
      var _tmpRatMkCrr = this.findByLabel(labelMap, ["peak withstand current", "making current"]);

      // --- Populate display list ---
      this.dataStList.push({ key: "System Vol", value: _tmpSysVolt, section: 'General' });
      this.dataStList.push({ key: "Rated Vol", value: _tmpRatVolt, section: 'General' });
      this.dataStList.push({ key: "Dry Frq", value: _tmpDryFrq, section: 'General' });
      this.dataStList.push({ key: "Rated Imp Vol", value: _tmpRatImpVolt, section: 'General' });
      this.dataStList.push({ key: "Frq", value: _tmpFrq, section: 'General' });
      this.dataStList.push({ key: "Switchboard Curr Rating", value: _tmpSwtCrRat, section: 'General' });
      this.dataStList.push({ key: "Short Time Curr Rating", value: _tmpShtTimeCrRat, section: 'General' });
      this.dataStList.push({ key: "Dynamic Peak Curr", value: _tmpDynmPeakCrr, section: 'General' });
      this.dataStList.push({ key: "Ambient Temp Design", value: _tmpAmbTmp, section: 'General' });
      this.dataStList.push({ key: "Altitude", value: _tmpAlt, section: 'General' });
      this.dataStList.push({ key: "Internal Arc Rating", value: _tmpIntArc, section: 'General' });
      this.dataStList.push({ key: "Enclosure", value: _tmpEnclosure, section: 'Panel' });
      this.dataStList.push({ key: "Busbar System", value: _tmpBusbarSystem, section: 'BusBars' });
      this.dataStList.push({ key: "Spring Charging Motor", value: _tmpSprgChrgMotor, section: 'Control/Auxiliary Voltage Levels' });
      this.dataStList.push({ key: "Rated Making Current (kAp)", value: _tmpRatMkCrr, section: 'Circuit Breaker' });

      // --- Build import object ---
      _tmpDS.push(new DataSheetImportDef(_tmpSysVolt, _tmpRatVolt, _tmpDryFrq, _tmpRatImpVolt, _tmpFrq, _tmpSwtCrRat, _tmpShtTimeCrRat, _tmpDynmPeakCrr, _tmpAmbTmp, _tmpAlt, _tmpIntArc, _tmpEnclosure,
        _tmpBusbarSystem, _tmpSprgChrgMotor, _tmpRatMkCrr
      ));

      this.searchOpts.extractedDatasheets = _tmpDS;
      this.searchOpts.datasheets.push(..._tmpDS);

    };
    reader.readAsBinaryString(target.files[0]);
  }


  removeIt(_lnIt){
    while(this.searchOpts.datasheets.indexOf(_lnIt) >= 0){
      this.searchOpts.datasheets.splice(this.searchOpts.datasheets.indexOf(_lnIt), 1);
    }
  }

  getSDItemCountMessage():string{
    if(this.searchOpts.extractedDatasheets.length == 0){
      return "No datasheet extracted.";
    }
    else if(this.searchOpts.extractedDatasheets.length == this.searchOpts.datasheets.length){
      return "Showing " + this.searchOpts.extractedDatasheets.length + " datasheet extracted."
    }
    else{
      return "Selected " + this.searchOpts.datasheets.length + " of " + this.searchOpts.extractedDatasheets.length + " extracted.";
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  confirmClick() {
    this.data.sDataArray.push(...this.searchOpts.datasheets);
    // this.data.sDataArray.push(this.dataStList);
    this.dialogRef.close(true);
  }
  getUniqueSections(sdItm: any): string[] {
    const sections = new Set<string>();
    if (sdItm) {
      sdItm.forEach((item: any) => {
        if (item.section) {
          sections.add(item.section);
        }
      });
    }
    return Array.from(sections);
  }

  getDataBySection(sdItm: any, section: string): any[] {
    if (!sdItm) {
      return [];
    }
    return sdItm.filter((item: any) => item.section === section);
  }
}

export class DataSheetSearchParams{
    constructor(
        public datasheetExtractMessage: string,
        public extractedDatasheets: Array<DataSheetImportDef>,
        public datasheets: Array<DataSheetImportDef>
    ){}
}
